/**
 * What a queue row lets you do without leaving the inbox.
 *
 * A queue row clears by being ANSWERED, not by being read — so the answer has to be
 * reachable from the row. Before this, an invitation was a sentence with a link:
 * you left the inbox, found the page, and answered there, which is three steps for
 * a yes.
 *
 * DESCRIPTORS, NOT CALLS. This module says what an action is and where it goes; the
 * app executes it with its own client and its own router. That keeps the map pure —
 * one copy, testable without a server — and keeps three front ends from each
 * inventing their own idea of what "Accept" means.
 *
 * `request` is executed and then the row is resolved; `navigate` opens somewhere
 * that finishes the job (a consent screen cannot be answered from a plate).
 */

const ACTIONS = {
    // An invitation is answered in place. Accepting returns a redirect_url — joining
    // requires choosing what you share with the group, which is a consent screen and
    // not a button.
    group_invitation: (row) => [
        {
            id: 'accept',
            label: 'Accept',
            primary: true,
            request: {
                method: 'PUT',
                path: `/groups/invitations/${row.subject_id}`,
                body: { action: 'accept' },
            },
            followRedirect: true,
        },
        {
            id: 'decline',
            label: 'Decline',
            request: {
                method: 'PUT',
                path: `/groups/invitations/${row.subject_id}`,
                body: { action: 'decline' },
            },
        },
    ],

    // A friend request is the same shape as an invitation: declining is a button,
    // accepting is a screen. `PUT /follows/<id>/respond` REQUIRES a privacy realm
    // when the action is accept — what you share back with the person — and rejects
    // the call 400 MISSING_FIELD without one. So Accept sent from here as a bare
    // request could never succeed; it opens the consent screen that picks the realm
    // and answers the follow from there, which is what the People page has always
    // done with its "Accept & Choose What to Share".
    //
    // A plain FOLLOW shares nothing back — it reaches your public page — so its
    // Accept is the answer itself. Opening the screen asked which realm to share,
    // which the server ignores for a follow: the person believed they had shared it.
    follow: (row) => [
        isPlainFollow(row)
            ? {
                id: 'accept',
                label: 'Accept',
                primary: true,
                request: {
                    method: 'PUT',
                    path: `/follows/${row.subject_id}/respond`,
                    body: { action: 'accept' },
                },
            }
            : {
                id: 'accept',
                label: 'Accept',
                primary: true,
                navigate: `/grant?follow_id=${row.subject_id}`
                    + `&redirect_uri=${encodeURIComponent('/manage?tab=people')}`,
            },
        {
            id: 'decline',
            label: 'Decline',
            request: {
                method: 'PUT',
                path: `/follows/${row.subject_id}/respond`,
                body: { action: 'decline' },
            },
        },
    ],
};

// Rows written before the payload carried the kind are told apart by their title.
const isPlainFollow = (row) => row.payload?.relationship_type === 'follow'
    || (!row.payload?.relationship_type && /wants to follow you$/.test(row.title || ''));

/**
 * The actions for one row. Empty for anything that is not a queue row, or that has
 * no answer the inbox can take.
 *
 * A consent screen is the clearest example of the second case: what an app may do
 * with somebody's data is not a thing to agree to from a plate, so those rows keep
 * a single Review that opens the real screen.
 */
export function actionsFor(row) {
    if (!row || row.lane !== 'queue') return [];

    const build = ACTIONS[row.subject_type];
    if (build && row.subject_id) return build(row);

    const consentUrl = row.payload?.consent_url;
    if (consentUrl) {
        return [{ id: 'review', label: 'Review', primary: true, navigate: consentUrl }];
    }

    return [];
}

/** True when a row can be answered where it stands. */
export function isAnswerable(row) {
    return actionsFor(row).some((action) => Boolean(action.request));
}


/**
 * Whether a person can dismiss this row. Only the feed (a queue row clears by being
 * answered; a security row is a record), and only a row addressed to them: a GROUP
 * row is shared by every member, and the server refuses to delete it for everyone
 * (409 NOT_DISMISSABLE) — offering the control there failed every time.
 */
export const canDismiss = (n) => n?.lane === 'feed' && (n?.audience || 'user') === 'user';
